"use client";

import type { AnchorHTMLAttributes, MouseEvent, ReactNode } from "react";
import Link from "next/link";
import { openPageDialog } from "./opener";
import type { PageDialogSettings } from "./settings";

/** A real link to the page, so a new tab, a crawler or a browser without JavaScript opens the page itself; a plain click
 *  opens it in the website's dialog instead. */
export default function PageDialogLink({
  href,
  dialog,
  children,
  onClick,
  ...rest
}: {
  href: string;
  dialog: PageDialogSettings;
  children: ReactNode;
} & Omit<AnchorHTMLAttributes<HTMLAnchorElement>, "href">) {
  const open = (event: MouseEvent<HTMLAnchorElement>) => {
    onClick?.(event);
    if (
      event.defaultPrevented ||
      event.button !== 0 ||
      event.metaKey ||
      event.ctrlKey ||
      event.shiftKey ||
      event.altKey
    )
      return;
    // Safari does not focus a clicked link, and the dialog returns focus to whatever had it when it opened.
    event.currentTarget.focus({ preventScroll: true });
    if (openPageDialog(href, dialog)) event.preventDefault();
  };
  return (
    <Link
      {...rest}
      href={href}
      aria-haspopup="dialog"
      data-page-dialog=""
      onClick={open}
    >
      {children}
    </Link>
  );
}
